<template>
  <VueDatePicker
    v-model="timePickerModelValue"
    :class="DS_TIMEPICKER_CLASSES.Root"
    data-cy="ds-time-picker__clock"
    inline
    :esc-close="false"
    time-picker
    auto-apply
    :is24="is24"
    :disabled="props.disabled"
  >
    <template #action-extra>
      <DSButton
        data-cy="ds-timepicker__now-button"
        size="sm"
        variant="secondary"
        @click="setNow"
      >
        {{ buttonNowText }}
      </DSButton>
    </template>

    <template #am-pm-button="{ toggle, value }">
      <DSButton
        data-cy="ds-time-picker__button-am-pm"
        @click="toggle"
      >
        {{ value }}
      </DSButton>
    </template>

    <template #arrow-up>
      <DSIcon icon="arrow-up" />
    </template>

    <template #arrow-down>
      <DSIcon icon="arrow-down" />
    </template>

    <template #clock-icon>
      <DSIcon icon="time-clock" />
    </template>

    <template #hours-overlay-value="{ text }">
      <DSButton variant="ghost">
        {{ text }}
      </DSButton>
    </template>

    <template #minutes-overlay-value="{ text }">
      <DSButton variant="ghost">
        {{ text }}
      </DSButton>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import VueDatePicker from "@vuepic/vue-datepicker";
import { computed } from "vue";
import dayjs from "dayjs";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import {
  DSTimePickerClockEmits,
  DSTimePickerClockProps,
} from "@/src/design_system/form_elements/timepicker/types/clock";
import type { DSDateTimePickerModelValue } from "@/src/design_system/form_elements/shared/types/dateTimePicker";
import { DS_TIMEPICKER_CLASSES } from "@/src/design_system/form_elements/timepicker/constants/classes";
import { easySettings } from "@/src/shared/store/easyGlobalSettings";

const props = defineProps<DSTimePickerClockProps>();

const emits = defineEmits<DSTimePickerClockEmits>();

const buttonNowText = window.I18n.designSystem.timePicker.buttonNow;

const setNow = () => {
  modelValue.value = dayjs();
  emits("close:popover");
};

const modelValue = defineModel<DSDateTimePickerModelValue>({ required: true });

const timePickerModelValue = computed({
  get: () =>
    modelValue.value
      ? {
          hours: modelValue.value.hour(),
          minutes: modelValue.value.minute(),
        }
      : null,
  set: (val: { hours: number; minutes: number } | null) => {
    if (!val) {
      modelValue.value = null;
      return;
    }

    const now = dayjs();
    modelValue.value = now.hour(val.hours).minute(val.minutes);
  },
});

const is24 = computed(() => {
  return !easySettings.getGlobalTimeFormat.value.includes("A");
});
</script>
